import type { Address } from 'viem';
import { supabase, TABLES, INVESTOR_SLOT_FIELDS } from './supabase';

export type InvestorTokenSource = 'tokens' | 'tokens_eth' | 'tokens_usd1' | 'tokens_sol' | 'unknown';

export interface InvestorSlotToken {
  slotIndex: number;
  slotAddress: string;
  chainType: 'evm' | 'solana' | 'unknown';
  chainId?: number;
  name: string;
  symbol: string;
  logo?: string;
  decimals?: number;
  source: InvestorTokenSource;
}

export interface RelayWalletToken {
  address: string;
  chainId: number;
  balanceRaw: bigint;
  balance: number;
  decimals: number;
  priceUSD?: number;
  valueUSD?: number;
  symbol?: string;
  name?: string;
  logo?: string;
}

export interface InvestorTokenMeta {
  name: string;
  symbol: string;
  logo?: string;
  decimals?: number;
  source: InvestorTokenSource;
}

export const extractInvestorSlots = (investor: Record<string, any>): string[] => {
  return INVESTOR_SLOT_FIELDS
    .map((field) => (investor[field] as string | null | undefined) || '')
    .map((slot) => slot.trim())
    .filter(Boolean);
};

const normalizeEvmAddress = (address: string) => address.toLowerCase();

const isEvmAddress = (address: string) => address.startsWith('0x');

const chunkArray = <T,>(items: T[], size: number) => {
  const result: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    result.push(items.slice(i, i + size));
  }
  return result;
};

export const fetchRelayWalletTokens = async (
  address: string,
  chainIds: number[],
  includeNative = false
): Promise<RelayWalletToken[]> => {
  if (!address || chainIds.length === 0) return [];

  try {
    const response = await fetch(
      `https://api.relay.link/currencies/user/${address}?chainIds=${chainIds.join(',')}&referrer=mavrk.ink`
    );
    if (!response.ok) return [];

    const data = await response.json();
    if (!Array.isArray(data)) return [];

    const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

    return data
      .filter((token: any) => token && typeof token.address === 'string')
      .filter((token: any) => includeNative || token.address.toLowerCase() !== ZERO_ADDRESS)
      .map((token: any) => {
        const decimals = typeof token.decimals === 'number' ? token.decimals : 18;
        const balanceRaw = BigInt(token.balance || '0');
        const balance = Number(balanceRaw) / Math.pow(10, decimals);
        const priceUSD = token.usdPrice ?? token.priceUsd ?? 0;
        return {
          address: token.address,
          chainId: token.chainId,
          balanceRaw,
          balance,
          decimals,
          priceUSD,
          valueUSD: balance * (priceUSD || 0),
          symbol: token.symbol,
          name: token.name,
          logo: token.logoURI || token.logo
        } as RelayWalletToken;
      })
      .filter((token) => includeNative || token.balanceRaw > 0n);
  } catch (error) {
    console.warn('[Investor] Failed to fetch Relay wallet tokens:', error);
    return [];
  }
};

export const fetchSupabaseEvmTokenMeta = async (addresses: string[]): Promise<Map<string, InvestorTokenMeta>> => {
  const normalized = addresses.map((addr) => addr.toLowerCase());
  const unique = Array.from(new Set(normalized));
  if (unique.length === 0) return new Map();

  const result = new Map<string, InvestorTokenMeta>();
  const chunks = chunkArray(unique, 100);

  const handleRows = (rows: any[], source: InvestorTokenSource) => {
    rows.forEach((token: any) => {
      const address = token.contract_address?.toLowerCase();
      if (!address) return;
      if (!result.has(address)) {
        result.set(address, {
          name: token.name || 'Unknown Token',
          symbol: token.symbol || 'UNKNOWN',
          logo: token.logo_url || undefined,
          decimals: token.decimals ?? undefined,
          source
        });
      }
    });
  };

  for (const chunk of chunks) {
    const [inkTokens, ethTokens, usd1Tokens] = await Promise.all([
      supabase
        .from(TABLES.TOKENS)
        .select('contract_address, name, symbol, logo_url, decimals')
        .in('contract_address', chunk),
      supabase
        .from(TABLES.TOKENS_ETH)
        .select('contract_address, name, symbol, logo_url, decimals')
        .in('contract_address', chunk),
      supabase
        .from(TABLES.TOKENS_USD1)
        .select('contract_address, name, symbol, logo_url, decimals')
        .in('contract_address', chunk)
    ]);

    if (inkTokens.error) console.warn('[Investor] Failed to fetch Ink tokens:', inkTokens.error);
    if (ethTokens.error) console.warn('[Investor] Failed to fetch ETH tokens:', ethTokens.error);
    if (usd1Tokens.error) console.warn('[Investor] Failed to fetch USD1 tokens:', usd1Tokens.error);

    handleRows(ethTokens.data || [], 'tokens_eth');
    handleRows(usd1Tokens.data || [], 'tokens_usd1');
    handleRows(inkTokens.data || [], 'tokens');
  }

  return result;
};

export const fetchSupabaseSolTokenMeta = async (mints: string[]): Promise<Map<string, InvestorTokenMeta>> => {
  const unique = Array.from(new Set(mints));
  if (unique.length === 0) return new Map();

  const result = new Map<string, InvestorTokenMeta>();
  const chunks = chunkArray(unique, 50);

  for (const chunk of chunks) {
    const query = chunk.join(',');
    const solTokens = await supabase
      .from(TABLES.TOKENS_SOL)
      .select('mint_address, contract_address, name, symbol, logo_url, decimals')
      .or(`mint_address.in.(${query}),contract_address.in.(${query})`);

    if (solTokens.error) {
      console.warn('[Investor] Failed to fetch Solana tokens:', solTokens.error);
      continue;
    }

    (solTokens.data || []).forEach((token: any) => {
      if (token.mint_address && !result.has(token.mint_address)) {
        result.set(token.mint_address, {
          name: token.name || 'Unknown Token',
          symbol: token.symbol || 'UNKNOWN',
          logo: token.logo_url || undefined,
          decimals: token.decimals ?? undefined,
          source: 'tokens_sol'
        });
      }
      if (token.contract_address && !result.has(token.contract_address)) {
        result.set(token.contract_address, {
          name: token.name || 'Unknown Token',
          symbol: token.symbol || 'UNKNOWN',
          logo: token.logo_url || undefined,
          decimals: token.decimals ?? undefined,
          source: 'tokens_sol'
        });
      }
    });
  }

  return result;
};

export const fetchInvestorSlotTokens = async (slotAddresses: string[]): Promise<InvestorSlotToken[]> => {
  if (slotAddresses.length === 0) return [];

  const evmAddresses = slotAddresses.filter(isEvmAddress).map(normalizeEvmAddress);
  const solAddresses = slotAddresses.filter((addr) => !isEvmAddress(addr));

  const [
    inkTokens,
    ethTokens,
    usd1Tokens,
    solTokens
  ] = await Promise.all([
    evmAddresses.length
      ? supabase
          .from(TABLES.TOKENS)
          .select('contract_address, name, symbol, logo_url, decimals')
          .in('contract_address', evmAddresses)
      : Promise.resolve({ data: [], error: null }),
    evmAddresses.length
      ? supabase
          .from(TABLES.TOKENS_ETH)
          .select('contract_address, name, symbol, logo_url, decimals')
          .in('contract_address', evmAddresses)
      : Promise.resolve({ data: [], error: null }),
    evmAddresses.length
      ? supabase
          .from(TABLES.TOKENS_USD1)
          .select('contract_address, name, symbol, logo_url, decimals')
          .in('contract_address', evmAddresses)
      : Promise.resolve({ data: [], error: null }),
    solAddresses.length
      ? supabase
          .from(TABLES.TOKENS_SOL)
          .select('mint_address, contract_address, name, symbol, logo_url, decimals')
          .or(`mint_address.in.(${solAddresses.join(',')}),contract_address.in.(${solAddresses.join(',')})`)
      : Promise.resolve({ data: [], error: null })
  ]);

  if (inkTokens.error) console.warn('[Investor] Failed to fetch Ink tokens:', inkTokens.error);
  if (ethTokens.error) console.warn('[Investor] Failed to fetch ETH tokens:', ethTokens.error);
  if (usd1Tokens.error) console.warn('[Investor] Failed to fetch USD1 tokens:', usd1Tokens.error);
  if (solTokens.error) console.warn('[Investor] Failed to fetch Solana tokens:', solTokens.error);

  const inkMap = new Map<string, any>();
  (inkTokens.data || []).forEach((token: any) => inkMap.set(token.contract_address?.toLowerCase(), token));

  const ethMap = new Map<string, any>();
  (ethTokens.data || []).forEach((token: any) => ethMap.set(token.contract_address?.toLowerCase(), token));

  const usd1Map = new Map<string, any>();
  (usd1Tokens.data || []).forEach((token: any) => usd1Map.set(token.contract_address?.toLowerCase(), token));

  const solMap = new Map<string, any>();
  (solTokens.data || []).forEach((token: any) => {
    if (token.mint_address) solMap.set(token.mint_address, token);
    if (token.contract_address) solMap.set(token.contract_address, token);
  });

  return slotAddresses.map((slotAddress, index) => {
    if (isEvmAddress(slotAddress)) {
      const lookup = slotAddress.toLowerCase();
      const token = ethMap.get(lookup) || usd1Map.get(lookup) || inkMap.get(lookup);
      const source: InvestorTokenSource =
        token && ethMap.has(lookup) ? 'tokens_eth' :
        token && usd1Map.has(lookup) ? 'tokens_usd1' :
        token && inkMap.has(lookup) ? 'tokens' :
        'unknown';

      const chainId = source === 'tokens' ? 57073 : 1;

      return {
        slotIndex: index,
        slotAddress,
        chainType: 'evm',
        chainId,
        name: token?.name || 'Unknown Token',
        symbol: token?.symbol || 'UNKNOWN',
        logo: token?.logo_url || undefined,
        decimals: token?.decimals ?? undefined,
        source
      } as InvestorSlotToken;
    }

    const solToken = solMap.get(slotAddress);
    return {
      slotIndex: index,
      slotAddress,
      chainType: solToken ? 'solana' : 'unknown',
      chainId: solToken ? 792703809 : undefined,
      name: solToken?.name || 'Unknown Token',
      symbol: solToken?.symbol || 'UNKNOWN',
      logo: solToken?.logo_url || undefined,
      decimals: solToken?.decimals ?? undefined,
      source: solToken ? 'tokens_sol' : 'unknown'
    };
  });
};

export const groupEvmSlotsByChain = (slots: InvestorSlotToken[]) => {
  const result = new Map<number, InvestorSlotToken[]>();
  slots
    .filter((slot) => slot.chainType === 'evm' && slot.chainId)
    .forEach((slot) => {
      const chainId = slot.chainId as number;
      if (!result.has(chainId)) {
        result.set(chainId, []);
      }
      result.get(chainId)!.push(slot);
    });
  return result;
};

export const toEvmAddress = (address: string): Address => address as Address;
